import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import useConferenceScenario from '../../hooks/useConferenceScenario';
import useConferenceSync from '../../hooks/useConferenceSync';
import mockGrid from '../../data/mockGrid.json';
import { slidesData } from '../../data/slidesData';
import PresentationSlides from './slides';
import {
    ClickEffect,
    GameMapView,
    NukePopin,
    NukeModal,
    QROverlay,
    UsineFrame,
    QrSallePleinEcran,
} from './components';
import { conferenceStyles } from './styles';
import { prechargerMedia, versionLocale } from './mediasEnMemoire';

/**
 * LA VOIX IA EN VIDÉO — plein écran, par-dessus tout, déclenchée par F1.
 *
 * POURQUOI ELLE EXISTE. Les 4 autres répliques de la voix IA sont des MP3 : un
 * son sort, l'orbe du 1er ministre s'anime, la slide reste à l'écran. La
 * première réplique, celle qui ouvre la conférence, est maintenant une VIDÉO
 * (23/09) — il n'y a donc rien à animer sur la slide, c'est la vidéo qui tient
 * l'écran pendant qu'elle parle.
 *
 * ⚠️ NOIR OPAQUE, PAS UN VOILE. Le titre de la slide 1 resterait lisible par
 * transparence et se retrouverait derrière le visage : on couvre tout. Le
 * retour sur la slide, à la fin, fait l'effet d'ouverture — c'est le moment où
 * Adrien prend la parole.
 *
 * ⚠️ CHROME PEUT REFUSER LE SON, ET C'EST LE PIÈGE DE CETTE SLIDE. Il
 * n'autorise `play()` non muet que si la page a DÉJÀ reçu une interaction — un
 * clic, une touche. Or la slide 1 est la toute première : Adrien ouvre le
 * diaporama et appuie sur F1, parfois sans avoir cliqué nulle part. La touche
 * F1 elle-même arrive trop tard, parce que `play()` part d'un effet React,
 * après le gestionnaire.
 *
 * La première version refermait l'écran en pareil cas. Résultat observé le
 * 23/09 : F1, et rien — pas de vidéo, pas de son, pas d'explication. On fait
 * donc l'inverse : **on garde l'écran**, on affiche la première image, et on
 * réessaie au prochain geste, quel qu'il soit. Le geste suivant est presque
 * toujours le second F1 d'Adrien, qui croit avoir raté le premier. Il n'y a
 * plus de cas où il ne se passe rien.
 *
 * ⚠️ PAS DE `controls`. Une barre de lecture qui apparaît au moindre mouvement
 * de souris, sur un vidéoprojecteur, transforme la réplique en démo technique.
 * Les commandes sont F1 (couper) et Échap, comme partout ailleurs.
 */
const VoixVideo = ({ url, onFin, onEchec }) => {
    const lecteur = useRef(null);
    const [bloquee, setBloquee] = useState(false);

    /* ⚠️ LES CALLBACKS PASSENT PAR UNE REF, ET CE N'EST PAS UN DÉTAIL. Le
       parent les recrée à chaque rendu — et il se rend souvent : les logs
       défilent, la souris bouge. Mises en dépendance de l'effet ci-dessous,
       elles le relanceraient à chaque fois, donc `currentTime = 0` : la vidéo
       repartirait du début au premier mouvement de souris, en pleine
       réplique. L'effet ne dépend donc que de `url`. */
    const rappels = useRef({ onFin, onEchec });
    rappels.current = { onFin, onEchec };

    useEffect(() => {
        const video = lecteur.current;
        if (!video) return undefined;

        let vivant = true;

        const lancer = () => video.play()
            .then(() => { if (vivant) setBloquee(false); })
            .catch(() => { /* la cause est traitée par le contrôle ci-dessous */ });

        /* ⚠️ `autoPlay` NE SUFFIT PAS, ET C'EST STRICTMODE QUI LE DIT. En
           développement, React monte l'effet, le démonte, puis le remonte : le
           nettoyage ci-dessous met la vidéo en PAUSE, et `autoPlay`, qui ne
           joue qu'à l'insertion de l'élément, ne la relance jamais. Observé le
           23/09 : écran noir, première image figée, son muet. On relance donc
           explicitement à chaque passage de l'effet. */
        lancer();

        /* Le filet : si le navigateur a refusé le son — la page n'avait encore
           reçu aucune interaction —, on réessaie au prochain geste. L'écoute
           est en CAPTURE pour passer avant le gestionnaire F1 du scénario, qui
           couperait la vidéo au lieu de la lancer. */
        const rattraper = () => { if (video.paused) lancer(); };
        const controle = setTimeout(() => {
            if (!vivant || !video.paused) return;
            setBloquee(true);
            rappels.current.onEchec?.(
                "SYSTEM: le navigateur a bloqué le son — appuyer à nouveau sur F1.");
        }, 400);

        window.addEventListener('keydown', rattraper, true);
        window.addEventListener('pointerdown', rattraper, true);

        return () => {
            vivant = false;
            clearTimeout(controle);
            window.removeEventListener('keydown', rattraper, true);
            window.removeEventListener('pointerdown', rattraper, true);
            video.pause();
        };
    }, [url]);

    return (
        <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center animate-fade-in">
            <video
                ref={lecteur}
                src={url}
                /* `autoPlay` en attribut, comme `IntroVideoSlide` du deck
                   « architecte », qui marche en scène depuis des mois : le
                   navigateur démarre lui-même, sans dépendre du moment où un
                   effet React s'exécute. */
                autoPlay
                onEnded={() => rappels.current.onFin?.()}
                onError={() => {
                    rappels.current.onEchec?.("SYSTEM: vidéo d'intro introuvable — reprendre à la voix.");
                    rappels.current.onFin?.();
                }}
                playsInline
                /* `object-contain` : la vidéo est en 1280×720, l'écran de la
                   salle ne l'est pas forcément. On préfère deux bandes noires
                   à un visage rogné. */
                className="w-full h-full object-contain"
            />

            {/* Discret, en bas : la salle regarde l'image, pas ce coin-là, et
                Adrien a besoin de savoir quoi faire sans quitter la scène. */}
            {bloquee && (
                <p className="absolute bottom-8 left-0 right-0 text-center text-slate-400 font-mono text-sm">
                    Appuyez sur une touche pour lancer le son
                </p>
            )}
        </div>
    );
};

const ConferencePage = ({ slides = slidesData, conferenceId = 'defaut', conducteur = null, logo = null }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    // Le QR des questions, en grand : ouvert et fermé depuis le prompteur.
    const [qrSalleOuvert, setQrSalleOuvert] = useState(false);
    const {
        logs,
        isTalking,
        mapInteraction,
        showNukeModal,
        showQrOverlay,
        showVideoOverlay,
        setShowVideoOverlay,
        videoVoix,
        setVideoVoix,
        showBugVisualization,
        setShowBugVisualization,
        isAITakeover,
        setIsAITakeover,
        addLog,
        latency,
        isAnalyzing,
        nuclearScore,
        setNuclearScore,
        isTargetingMode,
        setIsTargetingMode,
        showNukePopin,
        setShowNukePopin,
        selectedTarget,
        setSelectedTarget,
        glassBroken,
        focusClimate,
        analyserRef,
        triggerReaction
    } = useConferenceScenario(slides[currentSlide]);

    /* ⚠️ LES MÉDIAS DE SCÈNE SONT TÉLÉCHARGÉS À L'OUVERTURE, PAS AU F1.
       Vidéo de la voix IA et voix MP3 : le lecteur ne recevait son URL qu'au
       moment de jouer, d'où un démarrage lent et des saccades en ligne. Tout
       part en mémoire dès maintenant ; le pourquoi est dans `mediasEnMemoire`. */
    useEffect(() => {
        slides.forEach((s) => {
            prechargerMedia(s.videoVoix);
            prechargerMedia(s.voix);
        });
    }, [slides]);

    const particles = useMemo(() => [...Array(15)].map(() => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${2 + Math.random() * 3}px`,
        height: `${2 + Math.random() * 3}px`,
        opacity: 0.2,
        animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
        animationDelay: `${Math.random() * 2}s`
    })), []);

    // Transition handling states
    const [transitionsEnabled, setTransitionsEnabled] = useState(false);
    const pendingSlideIndex = useRef(null);
    const prevIsTalking = useRef(false);

    // Dynamic font scaling for conference mode
    // Référence 2560px : réduit légèrement à 1920px, scale up au-delà pour 4K / très grands écrans
    // 1366px → ×0.85 (13.6px) | 1920px → ×0.85 (13.6px) | 2560px → ×1.0 (16px) | 3840px → ×1.5 (24px)
    useEffect(() => {
        const htmlEl = document.documentElement;
        const savedFontSize = htmlEl.style.fontSize;

        const updateFontSize = () => {
            const scale = Math.max(0.85, Math.min(window.innerWidth / 2560, 1.5));
            htmlEl.style.fontSize = `${16 * scale}px`;
        };

        updateFontSize();
        window.addEventListener('resize', updateFontSize);

        return () => {
            window.removeEventListener('resize', updateFontSize);
            htmlEl.style.fontSize = savedFontSize;
        };
    }, []);

    // Watch for end of speech to complete slide transition
    useEffect(() => {
        if (prevIsTalking.current && !isTalking && pendingSlideIndex.current !== null) {
            setCurrentSlide(pendingSlideIndex.current);
            pendingSlideIndex.current = null;
        }
        prevIsTalking.current = isTalking;
    }, [isTalking]);

    /* ------------------------------------------------------------------
       PROMPTEUR — diffusion de l'avancement vers le second onglet.

       On émet le NUMÉRO de slide (`num`), pas son rang dans le tableau : c'est
       `num` qui relie le deck au conducteur, et c'est lui qui reste stable si
       une slide est coupée. Le rang, lui, se décale.

       `bonjour` est répondu immédiatement : c'est ce qui permet d'ouvrir — ou
       de recharger — le prompteur EN COURS de conférence sans se retrouver
       devant un écran vide.
       ------------------------------------------------------------------ */
    const slideRef = useRef(currentSlide);
    useEffect(() => { slideRef.current = currentSlide; }, [currentSlide]);

    const navigationRef = useRef(null);
    /* Rempli par UsineFrame : `{ avancer() }`, la séquence de scène de la
       simulation. Null tant que la simulation n'est pas montée. */
    const usineCommandes = useRef(null);

    /* L'ACTION DE LA SLIDE, JOUÉE PAR LA FLÈCHE (24/09). Une slide qui déclare
       `action` — la voix IA (`"f1"`) ou l'analyse des questions
       (`"synthese"`) — consomme le premier appui sur « suivant » pour la jouer.
       La télécommande de scène n'a que « suivant » et « précédent » : c'était
       le seul geste de la conférence qui demandait encore un clavier.
       Remise à zéro à chaque changement de slide, pendant le rendu, comme
       l'étape ci-dessous : revenir sur une slide réarme sa réplique.
       L'action est publiée au prompteur avec l'état : son bouton « → » dit ce
       que fera le prochain appui. */
    const [actionFaite, setActionFaite] = useState({ slide: currentSlide, faite: false });
    if (actionFaite.slide !== currentSlide) {
        setActionFaite({ slide: currentSlide, faite: false });
    }
    const actionEnAttente = slides[currentSlide]?.action
        && !(actionFaite.slide === currentSlide && actionFaite.faite)
        ? slides[currentSlide].action
        : null;

    /* F1 au clavier (ou relayé par le prompteur) joue la réplique lui-même :
       il compte comme l'appui, sinon la flèche suivante la rejouerait. */
    useEffect(() => {
        const surF1 = (e) => {
            if (e.key !== 'F1' || slides[slideRef.current]?.action !== 'f1') return;
            setActionFaite({ slide: slideRef.current, faite: true });
        };
        window.addEventListener('keydown', surF1);
        return () => window.removeEventListener('keydown', surF1);
    }, [slides]);

    /* Identité de CET onglet sur le canal. Le prompteur ne pilote qu'un
       diaporama à la fois — celui qui est à l'écran — et adresse ses ordres
       par `cible` : un deuxième onglet ouvert ne doit plus rien exécuter.
       Voir la note « UN SEUL DIAPORAMA EST PILOTÉ » dans PrompterPage. */
    const [instanceId] = useState(() => Math.random().toString(36).slice(2));

    const surMessagePrompteur = useCallback((msg) => {
        if (!msg || typeof msg !== 'object') return;
        // Sans `cible` : un prompteur d'avant le 23/09, on obéit comme avant.
        if (msg.cible && msg.cible !== instanceId) return;
        if (msg.t === 'bonjour') {
            emettreRef.current?.();
        } else if (msg.t === 'nav' && typeof msg.delta === 'number') {
            navigationRef.current?.(msg.delta, msg.forcer === true);
        } else if (msg.t === 'f1') {
            /* On REJOUE la touche au lieu d'appeler l'action directement.
               F1 n'est pas une commande unique : `useConferenceScenario` y
               branche la voix IA, mais aussi la casse du verre, la
               visualisation de bug, l'outro et le QR — selon le TITRE de la
               slide. Rebrancher tout ça ici en dupliquerait la table, et les
               deux copies divergeraient à la première slide ajoutée.
               L'événement synthétique passe par le même écouteur `window` que
               la vraie touche, donc le prompteur ne peut pas se désynchroniser
               du clavier. C'est déjà le procédé d'AIMinisterPanel. */
            window.dispatchEvent(new KeyboardEvent('keydown', {
                key: 'F1', bubbles: true, cancelable: true
            }));
        } else if (msg.t === 'qr') {
            /* Le QR des questions, en grand, par-dessus la slide affichée.
               Piloté depuis le prompteur parce qu'une salle ne répond pas
               toute en même temps : il faut pouvoir le remontrer à la minute
               35 sans revenir à la slide 3, ce qui annoncerait à tout le monde
               qu'on recule. Voir QrSallePleinEcran. */
            setQrSalleOuvert(msg.actif !== false);
        }
    }, [instanceId]);

    const envoyerSync = useConferenceSync(conferenceId, surMessagePrompteur);

    const emettreRef = useRef(null);
    emettreRef.current = () => {
        const s = slides[slideRef.current];
        envoyerSync({
            t: 'etat',
            index: slideRef.current,
            num: s?.num ?? slideRef.current + 1,
            total: slides.length,
            titre: s?.title || '',
            action: actionEnAttente,
            qr: qrSalleOuvert,
            source: instanceId,
            visible: document.visibilityState !== 'hidden'
        });
    };

    useEffect(() => {
        emettreRef.current?.();
    }, [currentSlide, slides, envoyerSync, actionEnAttente, qrSalleOuvert]);

    /* ⚠️ LA VIDÉO DE LA VOIX IA REFERME LE QR EN GRAND. L'accueil se fait QR
       plein écran (z-120), puis la flèche de la slide 1 lance la vidéo (z-60) :
       oublier de refermer le QR, c'était une vidéo jouée dessous, le son sans
       l'image. L'état part au prompteur avec `etat`, son bouton s'éteint. */
    useEffect(() => {
        if (videoVoix) setQrSalleOuvert(false);
    }, [videoVoix]);

    /* Passer à l'écran (ou en sortir) se signale : c'est ce qui permet au
       prompteur de choisir l'onglet projeté quand il y en a plusieurs. */
    useEffect(() => {
        const signaler = () => emettreRef.current?.();
        document.addEventListener('visibilitychange', signaler);
        return () => document.removeEventListener('visibilitychange', signaler);
    }, []);

    /* Prévenir le prompteur que le diaporama s'en va : sans ça, son voyant
       resterait au vert devant un onglet fermé.

       ⚠️ SUR `pagehide`, PAS AU DÉMONTAGE. Deux raisons, et les deux ont mordu :
       le nettoyage React s'exécute APRÈS celui du canal (ordre de déclaration),
       donc le message partait dans un canal déjà fermé ; et quand l'utilisateur
       ferme simplement l'onglet, React ne démonte rien du tout. `pagehide`
       couvre les deux, et se déclenche pendant que le canal est encore ouvert. */
    useEffect(() => {
        const adieu = () => envoyerSync({ t: 'adieu', source: instanceId });
        window.addEventListener('pagehide', adieu);
        return () => window.removeEventListener('pagehide', adieu);
    }, [envoyerSync, instanceId]);

    /* LES ÉTAPES À L'INTÉRIEUR D'UNE SLIDE. Une slide qui déclare `etapes: n`
       (aujourd'hui : le château de cartes de la slide 27, 3 appuis) consomme
       les n premiers appuis sur la flèche droite — effondrement,
       reconstruction, note — avant de changer de slide. Même logique que la
       séquence de l'usine : le
       présentateur n'a qu'une touche, c'est la slide qui sait si elle a encore
       quelque chose à montrer.
       L'étape est rangée avec l'index de sa slide ; le château, lui, se
       remonte (et se reconstruit) par le `slideKey` du diaporama.
       ⚠️ Posé dans `handleSlideNavigation` et non dans l'écouteur clavier : le
       clavier, les boutons et le prompteur passent tous par ici. */
    /* ⚠️ REMISE À ZÉRO À CHAQUE CHANGEMENT DE SLIDE, y compris un aller-retour
       (27 → 26 → 27) : on revient sur le château en construction, pas sur
       son dernier appui. Faite PENDANT le rendu (état dérivé, motif React
       documenté) et non dans un effet : un effet laisserait passer une image
       de l'état final avant de réinitialiser. */
    const [etape, setEtape] = useState({ slide: currentSlide, n: 0 });
    if (etape.slide !== currentSlide) {
        setEtape({ slide: currentSlide, n: 0 });
    }
    const etapeCourante = etape.slide === currentSlide ? etape.n : 0;
    const etapesDeLaSlide = slides[currentSlide]?.etapes || 0;

    /* Jouer l'action de la slide. La voix passe par une touche F1
       SYNTHÉTIQUE, comme le F1 relayé par le prompteur — voir la note sur
       `msg.t === 'f1'` : la table des répliques vit dans le scénario, on ne la
       duplique pas ici. L'analyse, elle, part au PROMPTEUR : c'est lui qui
       tient la session admin et l'état de la génération. */
    const jouerAction = (action) => {
        setActionFaite({ slide: currentSlide, faite: true });
        if (action === 'f1') {
            window.dispatchEvent(new KeyboardEvent('keydown', {
                key: 'F1', bubbles: true, cancelable: true
            }));
        } else if (action === 'synthese') {
            envoyerSync({ t: 'synthese', source: instanceId });
            addLog("SYSTEM: analyse des questions demandée au prompteur.");
        }
    };

    const handleSlideNavigation = (newIndex, forcer = false) => {
        const slideData = slides[currentSlide];

        /* L'action passe AVANT les étapes : aucune slide n'a aujourd'hui les
           deux, mais si ça arrive, la réplique ouvre la slide. `forcer` (le
           « ⏭ passer » du prompteur) la saute, comme il saute tout le reste. */
        if (!forcer && newIndex === currentSlide + 1 && actionEnAttente) {
            jouerAction(actionEnAttente);
            return;
        }

        if (!forcer && newIndex === currentSlide + 1 && etapeCourante < etapesDeLaSlide) {
            setEtape({ slide: currentSlide, n: etapeCourante + 1 });
            return;
        }

        if (transitionsEnabled && newIndex > currentSlide && slideData?.transition) {
            addLog(`%c[SYSTEM]%c Initialisation de la transition...`, "color: #3b82f6; font-weight: bold", "color: #fff");
            pendingSlideIndex.current = newIndex;
            triggerReaction(slideData.transition, 'tts');
        } else {
            setCurrentSlide(newIndex);
            pendingSlideIndex.current = null;
        }
    };

    /* Le prompteur pilote le diaporama par le MÊME chemin que la flèche du
       clavier : il hérite donc des transitions et des bornes, sans les redire.

       ⚠️ Y COMPRIS LA PRIORITÉ DE LA SÉQUENCE D'USINE, et c'est ce qui manquait.
       Sur le diaporama, la flèche droite demande d'abord à la simulation si elle
       consomme l'appui — c'est ce qui déroule la visite du plateau étage par
       étage, puis les tours. Depuis le prompteur, la même flèche partait
       directement à la slide suivante : la visite était sautée, et le seul moyen
       de la dérouler était de revenir sur l'onglet du diaporama pour y appuyer
       — exactement le geste que le prompteur existe pour éviter.

       La flèche GAUCHE reste l'issue de secours : elle ne rembobine pas la
       machine, elle change de slide, toujours.

       ET `forcer` EST LA SORTIE DE RÉPÉTITION. La priorité ci-dessus est la
       bonne en séance et la mauvaise en répétition : quand on reprend au milieu
       du déroulé, atteindre la slide d'après une slide d'usine imposait de
       re-dérouler la visite du plateau ou les cinq tours en entier. Le prompteur
       peut donc demander explicitement de ne pas consulter la séquence. Ça ne
       touche pas la machine — elle reste où elle en est, et revenir sur la slide
       la reprend par `enter()`. */
    navigationRef.current = (delta, forcer = false) => {
        if (!forcer && delta > 0 && usineCommandes.current?.avancer()) return;
        handleSlideNavigation(
            Math.min(slides.length - 1, Math.max(0, slideRef.current + delta)),
            forcer
        );
    };

    // Mock data generation
    const generateMockData = () => {
        const countriesData = [
            { id: 1, name: "Zhongo", pnj: 1 },
            { id: 2, name: "Eurocorp", pnj: 0 },
            { id: 3, name: "Petrolistan", pnj: 1 },
            { id: 4, name: "Volgovie", pnj: 1 },
            { id: 5, name: "Autoristan", pnj: 1 },
            { id: 6, name: "Sudica", pnj: 1 },
            { id: 7, name: "Neo Atlas", pnj: 1 },
            { id: 8, name: "Bricologia", pnj: 1 }
        ];

        const relationsMatrix = {
            "Zhongo": [100, 45, 60, 80, 40, 50, 40, 60],
            "Eurocorp": [45, 100, 60, 30, 10, 55, 90, 60],
            "Petrolistan": [60, 60, 100, 50, 80, 60, 30, 70],
            "Volgovie": [80, 30, 50, 100, 70, 60, 10, 50],
            "Autoristan": [40, 10, 80, 70, 100, 60, 40, 60],
            "Sudica": [50, 55, 60, 60, 60, 100, 50, 70],
            "Neo Atlas": [40, 90, 30, 10, 40, 50, 100, 60],
            "Bricologia": [35, 60, 70, 50, 60, 70, 60, 100]
        };

        const countries = {};
        const countryNamesList = ["Zhongo", "Eurocorp", "Petrolistan", "Volgovie", "Autoristan", "Sudica", "Neo Atlas", "Bricologia"];

        countriesData.forEach((c, index) => {
            const name = c.name;
            const allies = [];
            const enemies = [];
            const scores = relationsMatrix[name];
            scores.forEach((score, targetIndex) => {
                if (index !== targetIndex) {
                    const targetName = countryNamesList[targetIndex];
                    if (score > 70) allies.push(targetName);
                    else if (score < 40) enemies.push(targetName);
                }
            });

            countries[name] = {
                id: c.id,
                pnj: c.pnj,
                allies: allies,
                enemies: enemies
            };
        });

        // Assigner 2-3 villes par pays réparties sur le territoire
        const cellsByCountry = {};
        mockGrid.forEach(cell => {
            if (!cellsByCountry[cell.country]) cellsByCountry[cell.country] = [];
            cellsByCountry[cell.country].push(cell);
        });
        const cityCells = new Set();
        Object.values(cellsByCountry).forEach(cells => {
            // Trier par position pour un placement déterministe
            const sorted = [...cells].sort((a, b) => a.x - b.x || a.y - b.y);
            // Placer des villes à ~25%, ~50%, ~75% du territoire
            const count = Math.min(3, Math.max(1, Math.floor(cells.length / 10)));
            for (let i = 0; i < count; i++) {
                const idx = Math.floor((i + 1) * sorted.length / (count + 1));
                const c = sorted[idx];
                cityCells.add(`${c.x},${c.y}`);
            }
        });

        const formattedGrid = mockGrid.map(cell => ({
            ...cell,
            new: false || cell.new,
            lose: false,
            resources: cell.resources || Math.floor(Math.random() * 20),
            city: cell.city || cityCells.has(`${cell.x},${cell.y}`),
        }));

        return { countries, grid: formattedGrid };
    };

    const [mockData] = useState(() => generateMockData());
    const [showGameUI, setShowGameUI] = useState(false);
    const wasVideoPlaying = useRef(false);

    // La simulation d'usine n'est chargée que pour les conférences qui en contiennent.
    const hasUsine = slides.some(s => s.template === 'factory' && s.variant !== 'curve');
    // Le repli dessiné par la slide ne doit apparaître que si la simulation a
    // vraiment échoué : sinon on le voit passer pendant le fondu de l'iframe.
    const [usineEnPanne, setUsineEnPanne] = useState(false);
    const showUsine = slides[currentSlide]?.template === 'factory'
        && slides[currentSlide]?.variant !== 'curve';

    useEffect(() => {
        if (showVideoOverlay) {
            wasVideoPlaying.current = true;
        } else if (wasVideoPlaying.current) {
            setCurrentSlide(slides.length - 1);
            setShowGameUI(false);
            wasVideoPlaying.current = false;
        }
    }, [showVideoOverlay]);

    /* Le bouton du prompteur ne peut pas être permanent : cette page EST ce que
       la salle voit. Il se comporte donc comme les commandes d'un lecteur vidéo
       — visible tant que la souris bouge, effacé au bout de trois secondes
       d'immobilité. On l'ouvre une fois, avant de commencer. */
    const [sourisActive, setSourisActive] = useState(true);
    useEffect(() => {
        let t;
        const bouge = () => {
            setSourisActive(true);
            clearTimeout(t);
            t = setTimeout(() => setSourisActive(false), 3000);
        };
        bouge();
        window.addEventListener('mousemove', bouge);
        return () => { window.removeEventListener('mousemove', bouge); clearTimeout(t); };
    }, []);

    return (
        <div className="w-full h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden relative font-sans">
            <ClickEffect />

            {conducteur && (
                <button
                    onClick={() => window.open(`/conference/${conferenceId}/prompteur`, '_blank', 'noopener')}
                    className={`absolute bottom-4 left-4 z-50 px-3 py-2 rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-white/10 text-xs text-slate-300 backdrop-blur transition-opacity duration-500 ${sourisActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    title="Ouvre le conducteur dans un second onglet, synchronisé avec ce diaporama"
                >
                    📄 Prompteur
                </button>
            )}
            <style>{conferenceStyles}</style>

            {/* MAIN CONTENT CONTAINER */}
            {/* Pendant la vidéo de la voix IA, la page dessous est masquée en
                `visibility`, pas en `display` : le navigateur cesse de peindre
                les particules et l'orbe de la slide de titre — du travail en
                moins pendant le décodage —, mais la mise en page ne bouge pas,
                donc l'iframe de l'usine ne se voit pas redimensionnée à zéro. */}
            <div className={`w-full h-screen transition-opacity duration-500 ${showVideoOverlay ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} style={{ display: showVideoOverlay ? 'none' : 'block', visibility: videoVoix ? 'hidden' : 'visible' }}>
                {/* Background grid */}
                <div className="absolute inset-0 opacity-5 pointer-events-none">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `
                            linear-gradient(rgba(59, 130, 246, 0.5) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(59, 130, 246, 0.5) 1px, transparent 1px)
                        `,
                        backgroundSize: '50px 50px'
                    }} />
                </div>

                {/* Floating particles — positions figées au montage.
                    Tirer les `Math.random()` dans le JSX les retirait à CHAQUE
                    rendu : les particules sautaient et leurs animations CSS
                    repartaient de zéro, forçant un recalcul de style sur toute
                    la page. Or la page se re-rend toutes les 8 secondes (boucle
                    de logs du hook). */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {particles.map((p, i) => (
                        <div
                            key={i}
                            className="absolute rounded-full bg-blue-500"
                            style={p}
                        />
                    ))}
                </div>

                {/* Main content - full width */}
                <div className="p-4 h-full">
                    <div className="w-full h-full relative flex flex-col min-h-0 overflow-hidden">
                        {(showGameUI || isTargetingMode) ? (
                            <GameMapView
                                mockData={mockData}
                                isTalking={isTalking}
                                mapInteraction={mapInteraction}
                                isTargetingMode={isTargetingMode}
                                setIsTargetingMode={setIsTargetingMode}
                                setSelectedTarget={setSelectedTarget}
                                addLog={addLog}
                            />
                        ) : (
                            <PresentationSlides
                                slides={slides}
                                logo={logo}
                                showFactoryFallback={!hasUsine || usineEnPanne}
                                currentSlide={currentSlide}
                                etape={etapeCourante}
                                onSlideChange={handleSlideNavigation}
                                showBugVisualization={showBugVisualization}
                                setShowBugVisualization={setShowBugVisualization}
                                isAITakeover={isAITakeover}
                                setIsAITakeover={setIsAITakeover}
                                nuclearScore={nuclearScore}
                                setShowNukePopin={setShowNukePopin}
                                selectedTarget={selectedTarget}
                                mapData={mockData}
                                transitionsEnabled={transitionsEnabled}
                                onToggleTransitions={() => setTransitionsEnabled(!transitionsEnabled)}
                                triggerReaction={triggerReaction}
                                isTalking={isTalking}
                                glassBroken={glassBroken}
                                focusClimate={focusClimate}
                                analyserRef={analyserRef}
                            />
                        )}

                        {hasUsine && (
                            <UsineFrame
                                visible={showUsine}
                                commandes={usineCommandes}
                                /* Numéro du PASSAGE dans la simulation, porté par
                                   la slide (`usine: 1 | 2 | 3`) et non déduit de
                                   son rang : les slides se renumérotent à chaque
                                   remontage du déroulé, la séquence de scène non. */
                                segment={slides[currentSlide]?.usine}
                                onStatus={(ok) => setUsineEnPanne(!ok)}
                                onNavigate={(delta) => handleSlideNavigation(
                                    Math.min(slides.length - 1, Math.max(0, currentSlide + delta))
                                )}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Overlays */}
            {showNukePopin && (
                <NukePopin
                    nuclearScore={nuclearScore}
                    setNuclearScore={setNuclearScore}
                    addLog={addLog}
                    setShowNukePopin={setShowNukePopin}
                    setSelectedTarget={setSelectedTarget}
                    setIsTargetingMode={setIsTargetingMode}
                />
            )}

            {showNukeModal && <NukeModal />}

            {showQrOverlay && <QROverlay />}

            {/* Le QR du formulaire, par-dessus n'importe quelle slide. Se
                referme d'un clic, ou par le même bouton du prompteur. */}
            {qrSalleOuvert && <QrSallePleinEcran onFermer={() => setQrSalleOuvert(false)} />}

            {videoVoix && (
                <VoixVideo url={versionLocale(videoVoix)} onFin={() => setVideoVoix(null)} onEchec={addLog} />
            )}
        </div>
    );
};

export default ConferencePage;
